import React, { MutableRefObject } from 'react';
import classNames from 'classnames';
import {
  LIST_OPTION_CLASS,
  LIST_OPTION_SELECTED_CLASS,
  LIST_OPTION_FOCUSED_CLASS,
  LIST_CLASS,
  LIST_OPTION_NO_RESULTS,
} from './ComboBoxClasses';
import { ComboBoxOption } from './ComboBoxOption';

interface ComboBoxOptionsProps {
  className?: string;
  id: string;
  listId: string;
  labelId: string;
  options: ComboBoxOption[];
  value: string | number | undefined;
  focusedIndex: number | null;
  noSelectionIndex: number;
  focusRef: MutableRefObject<HTMLLIElement | null>;
  showList: boolean;
  listRef: MutableRefObject<HTMLUListElement | null>;
  handleOptionClick: (option: ComboBoxOption) => void;
  handleOptionKeyDown: (event: React.KeyboardEvent<HTMLLIElement>, option: ComboBoxOption) => void;
  handleOptionMouseMove: (index: number) => void;
}

const ComboBoxOptions = ({
  className,
  id,
  listId,
  labelId,
  options,
  value,
  focusedIndex,
  noSelectionIndex,
  focusRef,
  showList,
  listRef,
  handleOptionClick,
  handleOptionKeyDown,
  handleOptionMouseMove,
}: ComboBoxOptionsProps): JSX.Element => {
  const optionElements = options.map((option, index) => {
    const selected = option.value === value;
    const noSelection = !value && focusedIndex === -1 && index === noSelectionIndex;
    const focused = index === focusedIndex || (focusedIndex === -1 && (selected || noSelection));
    const optionId = `${id}--option-${index}`;
    return (
      <li
        key={option.value}
        ref={focused ? focusRef : null}
        role="option"
        aria-selected={selected}
        aria-setsize={options.length}
        aria-posinset={index + 1}
        id={optionId}
        tabIndex={selected || noSelection ? 0 : -1}
        data-value={option.value}
        className={classNames({
          [LIST_OPTION_CLASS]: true,
          [LIST_OPTION_SELECTED_CLASS]: selected,
          [LIST_OPTION_FOCUSED_CLASS]: focused,
        })}
        onClick={(): void => handleOptionClick(option)}
        onKeyDown={(event): void => handleOptionKeyDown(event, option)}
        onMouseMove={(): void => handleOptionMouseMove(index)}
      >
        {option.text}
      </li>
    );
  });
  let listContents;
  if (showList) {
    listContents =
      optionElements.length > 0 ? optionElements : <li className={LIST_OPTION_NO_RESULTS}>No results found</li>;
  } else {
    listContents = null;
  }
  return (
    <ul
      ref={listRef}
      tabIndex={-1}
      id={listId}
      className={classNames(className, LIST_CLASS)}
      role="listbox"
      hidden={!showList}
      aria-labelledby={labelId}
    >
      {listContents}
    </ul>
  );
};

export default ComboBoxOptions;
